import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { parseGroupFields, SchemaGroup, skuSchemaClient } from '../../api/schema';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { useCan } from '../../store';
import FieldGroupEditorModal, {
    FieldGroupEditorState,
    fieldGroupEditorFrom,
    newFieldGroupEditor,
} from '../../components/schema/FieldGroupEditorModal';
import HelpTip from '../../components/HelpTip';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import { invalidateSkuSchemas } from '../../utils/skuSpec';

// SKU 规格模板 (2026-10-14): 规格类型 (板材 / 卷材 / 纸张 …) 与各自字段.
// 与材料 / 设备模板同一套字段组数据结构与编辑弹窗, 但没有作用域 (不按类别 / 框架分模板):
// SKU 自己选规格类型, 所以这里是一张平铺的类型表. 字段可标必填 —— 原材料 SKU 必须选类型并填齐必填项.

function errMsg(e: unknown): string {
    return e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
}

function SkuTemplateAdmin() {
    const canCreate = useCan('sku_schema', 'create');
    const canUpdate = useCan('sku_schema', 'update');
    const canDelete = useCan('sku_schema', 'delete');
    const history = useHistory();
    const [groups, setGroups] = useState<SchemaGroup[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editor, setEditor] = useState<FieldGroupEditorState | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setGroups(await skuSchemaClient.list());
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const onSaved = () => {
        setEditor(null);
        invalidateSkuSchemas();
        load();
    };

    const toggleEnabled = async (g: SchemaGroup) => {
        try {
            const res = await skuSchemaClient.update(g.groupKey, { isEnabled: !g.isEnabled });
            invalidateSkuSchemas();
            load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(`保存失败: ${errMsg(e)}`);
        }
    };

    const remove = async (g: SchemaGroup) => {
        if ((g.boundCount ?? 0) > 0) {
            alertDialog(
                `「${g.label}」还有 ${g.boundCount} 个 SKU 在用, 不能删除。可以先停用, 或把这些 SKU 改成别的规格类型。`,
            );
            return;
        }
        if (!(await confirmDialog(`确认删除规格类型「${g.label}」(${g.groupKey})?`))) return;
        try {
            const res = await skuSchemaClient.remove(g.groupKey);
            invalidateSkuSchemas();
            load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(`删除失败: ${errMsg(e)}`);
        }
    };

    return (
        <div className="space-y-4">
            <div className="card p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="text-base font-semibold text-slate-800">
                            SKU 规格模板
                            <HelpTip text="每种规格类型一组字段, 例 板材 (长 / 宽 / 厚度)、卷材 (幅宽 / 卷长)。SKU 在新建 / 编辑时选规格类型并填字段; 属性为「原材料」的 SKU 必须选类型并填齐必填项。字段 key 从 Schema 字段字典选。管理员改动即时生效, 普通用户提交走审批工单。" />
                        </h2>
                        <p className="text-xs text-slate-500 mt-1">
                            共 {groups.length} 个规格类型 · SKU 库可按规格类型筛选, 筛选后规格字段展开成独立列
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => setEditor(newFieldGroupEditor())}
                        hidden={!canCreate}
                    >
                        + 新建规格类型
                    </button>
                </div>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th">规格类型</th>
                            <th className="th">编码</th>
                            <th className="th">字段 (* 必填)</th>
                            <th className="th">在用 SKU</th>
                            <th className="th">状态</th>
                            <th className="th" hidden={!canUpdate && !canDelete}>
                                操作
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && groups.length === 0 && (
                            <tr>
                                <td className="td text-center text-slate-400" colSpan={6}>
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && groups.length === 0 && (
                            <tr>
                                <td className="td text-center text-slate-400" colSpan={6}>
                                    还没有规格类型
                                </td>
                            </tr>
                        )}
                        {groups.map((g) => (
                            <tr key={g.groupKey} className="hover:bg-slate-50">
                                <td className="td font-medium">{g.label}</td>
                                <td className="td font-mono text-xs text-slate-500">{g.groupKey}</td>
                                <td className="td">
                                    <div className="flex flex-wrap gap-1">
                                        {parseGroupFields(g.fields).map((f) => (
                                            <span
                                                key={f.key}
                                                className="tag bg-slate-100 text-slate-600"
                                                title={f.key}
                                            >
                                                {f.required && <span className="text-rose-500">* </span>}
                                                {f.label}
                                                {f.unit && <span className="text-slate-400"> ({f.unit})</span>}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="td text-sm">
                                    {(g.boundCount ?? 0) > 0 ? (
                                        <button
                                            type="button"
                                            className="text-brand-600 hover:underline"
                                            title="在 SKU 库里查看这些 SKU"
                                            onClick={() =>
                                                history.push(`/skus?specGroup=${encodeURIComponent(g.groupKey)}`)
                                            }
                                        >
                                            {g.boundCount} 个
                                        </button>
                                    ) : (
                                        <span className="text-slate-300">0</span>
                                    )}
                                </td>
                                <td className="td">
                                    <span className={`tag ${g.isEnabled ? 'cap-on' : 'bg-slate-100 text-slate-400'}`}>
                                        {g.isEnabled ? '启用' : '停用'}
                                    </span>
                                </td>
                                <td className="td text-xs whitespace-nowrap space-x-3" hidden={!canUpdate && !canDelete}>
                                    <button
                                        type="button"
                                        className="text-brand-600 hover:underline"
                                        onClick={() => setEditor(fieldGroupEditorFrom(g, false))}
                                        hidden={!canUpdate}
                                    >
                                        编辑
                                    </button>
                                    <button
                                        type="button"
                                        className="text-slate-500 hover:underline"
                                        onClick={() => toggleEnabled(g)}
                                        hidden={!canUpdate}
                                    >
                                        {g.isEnabled ? '停用' : '启用'}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-danger-text"
                                        onClick={() => remove(g)}
                                        hidden={!canDelete}
                                    >
                                        删除
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editor && (
                <FieldGroupEditorModal
                    editor={editor}
                    setEditor={setEditor}
                    onClose={() => setEditor(null)}
                    onSaved={onSaved}
                    client={skuSchemaClient}
                    domainLabel="SKU 规格"
                    fieldRequired
                />
            )}
        </div>
    );
}

export default SkuTemplateAdmin;
